import React from "react";
import { Checkbox, Grid, Stack, Text } from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import type { DayHours, DayKey, WeeklyHours } from "../../../types/staffForm";
import { DAYS, isDayEnabled } from "./staffFormUtils";

interface WeeklyHoursEditorProps {
  hours: WeeklyHours;
  onToggleDay: (day: DayKey, checked: boolean) => void;
  onTimeChange: (day: DayKey, side: keyof DayHours, value: string) => void;
}

/**
 * One row per weekday: a checkbox for "works this day" plus start/end times.
 * A checked day keeps its row checked even with blank times (a salon-closed
 * day has no hours to seed) — validation then asks for both times.
 */
export function WeeklyHoursEditor({
  hours,
  onToggleDay,
  onTimeChange,
}: WeeklyHoursEditorProps) {
  return (
    <Stack gap="xs">
      {DAYS.map(({ key, label }) => {
        const day = hours[key];
        const enabled = isDayEnabled(day);
        return (
          <Grid key={key} align="center" gutter="sm">
            <Grid.Col span={{ base: 12, xs: 4 }}>
              <Checkbox
                label={label}
                checked={enabled}
                color="teal"
                onChange={(event) =>
                  onToggleDay(key, event.currentTarget.checked)
                }
              />
            </Grid.Col>
            {enabled ? (
              <>
                <Grid.Col span={{ base: 6, xs: 4 }}>
                  <TimeInput
                    aria-label={`${label} start time`}
                    value={day.start || ""}
                    withSeconds={false}
                    step={900}
                    onChange={(event) =>
                      onTimeChange(key, "start", event.currentTarget.value)
                    }
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 6, xs: 4 }}>
                  <TimeInput
                    aria-label={`${label} end time`}
                    value={day.end || ""}
                    withSeconds={false}
                    step={900}
                    onChange={(event) =>
                      onTimeChange(key, "end", event.currentTarget.value)
                    }
                  />
                </Grid.Col>
              </>
            ) : (
              <Grid.Col span={{ base: 12, xs: 8 }} visibleFrom="xs">
                <Text size="sm" c="dimmed">
                  Unavailable
                </Text>
              </Grid.Col>
            )}
          </Grid>
        );
      })}
    </Stack>
  );
}
